/**
 * FHIR Client (Proxy-based)
 *
 * SECURITY ARCHITECTURE:
 * =====================
 * This client NEVER directly communicates with the FHIR server.
 * All requests go through the backend proxy, which:
 * - Adds the OAuth access token (invisible to frontend)
 * - Validates the session
 * - Handles token refresh automatically
 * - Enforces security policies
 *
 * The frontend only needs to know the session token (UUID).
 *
 * INTERCEPTOR INTEGRATION:
 * =======================
 * This client uses the centralized InterceptorClient (fhirApiClient) which provides:
 * - Automatic retry logic for transient failures
 * - Centralized error transformation
 * - Request/response logging (debug mode)
 * - Launch context injection (Epic Hyperspace support)
 * - Credential handling via cookies
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { refreshToken, getSessionInfo } from './smartAuthService';
import { fhirApiClient, isApiError } from '../interceptor/interceptor';
// ==========================================
// ERROR HANDLING
// ==========================================
export class FhirClientError extends Error {
    constructor(message, statusCode, operationOutcome) {
        super(message);
        this.statusCode = statusCode;
        this.operationOutcome = operationOutcome;
        this.name = 'FhirClientError';
    }
}
// ==========================================
// FHIR CLIENT
// ==========================================
export class FhirClient {
    constructor() {
        this.refreshing = false;
        // SECURITY: Session token is now in secure HTTP-only cookie
        // No need to load from storage
    }
    /**
     * @deprecated Session tokens are now in secure HTTP-only cookies.
     * This function does nothing.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setSessionToken(_token) {
        // Do nothing - session token is in HTTP-only cookie
    }
    /**
     * Clear session.
     * Call logout on backend to clear cookies.
     */
    clearSession() {
        // Session cleared by backend via logout endpoint
    }
    /**
     * Check if client is authenticated by calling backend.
     * @returns Promise<boolean>
     */
    isAuthenticated() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield getSessionInfo();
                return true;
            }
            catch (_a) {
                return false;
            }
        });
    }
    /**
     * Make a FHIR request through the proxy using the interceptor client.
     * SECURITY: Session token is in secure HTTP-only cookie, automatically sent.
     * The interceptor handles:
     * - Automatic retry logic for transient failures
     * - Launch context injection (Epic Hyperspace support)
     * - Centralized error transformation
     * - Request/response logging (debug mode)
     */
    request(method, path, data) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                let response;
                switch (method) {
                    case 'GET':
                        response = yield fhirApiClient.get(path);
                        break;
                    case 'POST':
                        response = yield fhirApiClient.post(path, data);
                        break;
                    case 'PUT':
                        response = yield fhirApiClient.put(path, data);
                        break;
                    case 'DELETE':
                        response = yield fhirApiClient.delete(path);
                        break;
                }
                // WORKAROUND: Backend sometimes returns JSON as a string instead of parsed object
                // This should be fixed in the backend, but we handle it here for robustness
                let responseData = response.data;
                if (typeof responseData === 'string') {
                    try {
                        responseData = JSON.parse(responseData);
                    }
                    catch (_a) {
                        throw new FhirClientError('Backend returned invalid JSON string');
                    }
                }
                return responseData;
            }
            catch (error) {
                // Handle ApiError from interceptor
                if (isApiError(error)) {
                    // Handle 401 - session expired, try to refresh
                    if (error.isSessionExpired && !this.refreshing) {
                        return yield this.retryWithRefresh(method, path, data);
                    }
                    // Check if response contains FHIR OperationOutcome
                    if (error.data && typeof error.data === 'object' && 'resourceType' in error.data) {
                        const responseData = error.data;
                        if (responseData.resourceType === 'OperationOutcome') {
                            const outcome = error.data;
                            const message = this.extractErrorMessage(outcome);
                            throw new FhirClientError(message, error.statusCode, outcome);
                        }
                    }
                    // Handle generic error response
                    if (error.data && typeof error.data === 'object' && 'error' in error.data) {
                        const errorObj = error.data;
                        throw new FhirClientError(errorObj.error_description || errorObj.error, error.statusCode);
                    }
                    throw new FhirClientError(error.message, error.statusCode);
                }
                throw new FhirClientError('Unexpected error during FHIR request');
            }
        });
    }
    /**
     * Retry request after refreshing token.
     * SECURITY: Session token is in secure HTTP-only cookie, automatically sent.
     */
    retryWithRefresh(method, path, data) {
        return __awaiter(this, void 0, void 0, function* () {
            this.refreshing = true;
            try {
                yield refreshToken(); // No need to pass or receive session token
                // Retry original request (cookies updated by backend)
                return yield this.request(method, path, data);
            }
            catch (_a) {
                throw new FhirClientError('Session expired - please re-authenticate');
            }
            finally {
                this.refreshing = false;
            }
        });
    }
    /**
     * Extract error message from OperationOutcome.
     */
    extractErrorMessage(outcome) {
        var _a;
        if (outcome.issue && outcome.issue.length > 0) {
            const issue = outcome.issue[0];
            return issue.diagnostics || ((_a = issue.details) === null || _a === void 0 ? void 0 : _a.text) || issue.code || 'FHIR operation failed';
        }
        return 'FHIR operation failed';
    }
    // ==========================================
    // PUBLIC API - READ OPERATIONS
    // ==========================================
    /**
     * Read a single resource by type and ID.
     *
     * @example
     * const patient = await client.read<PatientResource>('Patient', '123');
     * const patient = await client.read<PatientResource>('Patient', '123', { _elements: 'name,birthDate' });
     */
    read(resourceType, id, params) {
        return __awaiter(this, void 0, void 0, function* () {
            let path = `${resourceType}/${id}`;
            if (params) {
                const queryString = new URLSearchParams(Object.entries(params).map(([key, value]) => [key, String(value)])).toString();
                path += `?${queryString}`;
            }
            return this.request('GET', path);
        });
    }
    /**
     * Search for resources.
     *
     * @example
     * const bundle = await client.search<PatientResource>('Patient', { name: 'Smith' });
     */
    search(resourceType, params) {
        return __awaiter(this, void 0, void 0, function* () {
            let path = resourceType;
            if (params) {
                const queryString = new URLSearchParams(Object.entries(params).map(([key, value]) => [key, String(value)])).toString();
                path += `?${queryString}`;
            }
            return this.request('GET', path);
        });
    }
    /**
     * Get patient resource (convenience method).
     */
    getPatient(patientId, params) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.read('Patient', patientId, params);
        });
    }
    // ==========================================
    // PUBLIC API - WRITE OPERATIONS
    // ==========================================
    /**
     * Create a new resource.
     */
    create(resourceType, resource) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request('POST', resourceType, resource);
        });
    }
    /**
     * Update an existing resource.
     */
    update(resourceType, id, resource) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request('PUT', `${resourceType}/${id}`, resource);
        });
    }
    /**
     * Delete a resource.
     */
    delete(resourceType, id) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('DELETE', `${resourceType}/${id}`);
        });
    }
    // ==========================================
    // UTILITY METHODS
    // ==========================================
    /**
     * Extract patient name from Patient resource.
     * Falls back to identifier if name is not available.
     */
    extractPatientName(patient) {
        var _a;
        // Try to get name from name field
        if (patient.name && patient.name.length > 0) {
            // Find official name or use first name
            const name = patient.name.find((n) => n.use === 'official') || patient.name[0];
            // Use text field if available
            if (name.text) {
                return name.text;
            }
            // Construct from parts
            const given = name.given ? name.given.join(' ') : '';
            const family = name.family || '';
            const fullName = `${given} ${family}`.trim();
            if (fullName) {
                return fullName;
            }
        }
        // Fallback: Try to use identifier (Epic ID, External ID, etc.)
        if (patient.identifier && patient.identifier.length > 0) {
            // Prefer EPIC or EXTERNAL identifier
            const epicId = patient.identifier.find((id) => { var _a; return ((_a = id.type) === null || _a === void 0 ? void 0 : _a.text) === 'EPIC'; });
            const externalId = patient.identifier.find((id) => { var _a; return ((_a = id.type) === null || _a === void 0 ? void 0 : _a.text) === 'EXTERNAL'; });
            const preferredId = epicId || externalId || patient.identifier[0];
            if (preferredId === null || preferredId === void 0 ? void 0 : preferredId.value) {
                const idType = ((_a = preferredId.type) === null || _a === void 0 ? void 0 : _a.text) || 'ID';
                return `Patient ${idType}: ${preferredId.value}`;
            }
        }
        // Last resort: use patient ID
        if (patient.id) {
            return `Patient ID: ${patient.id}`;
        }
        return 'Unknown Patient';
    }
    /**
     * Format patient info for display.
     */
    formatPatientInfo(patient) {
        const name = this.extractPatientName(patient);
        const parts = [name];
        if (patient.birthDate) {
            parts.push(`DOB: ${patient.birthDate}`);
        }
        if (patient.gender) {
            parts.push(patient.gender);
        }
        return parts.join(' • ');
    }
}
// ==========================================
// SINGLETON INSTANCE
// ==========================================
/**
 * Default FHIR client instance.
 * Use this throughout your app for all FHIR requests.
 *
 * @example
 * import { fhirClient } from './services/fhirClient';
 *
 * const patient = await fhirClient.getPatient('123');
 */
export const fhirClient = new FhirClient();
